import Script from 'next/script';
import { FileDownloadService } from '../../services/file-download.service';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { Component } from 'react';
import { debounceTime } from 'rxjs/operators';
import ContactUsModal from '../../components/ui/contact-us-modal/contact-us.modal';
import { FileListComponent } from '../../components/ui/file-list/file-list.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { BulkZipService } from '../../services/bulk-zip.service';
import { bulkZipButtonText, bulkZipCoveredNote, bulkZipErrorText, bulkZipPlan } from './bulk-zip-plan';
import { FileManagerService } from '../../services/file-manager.service';
import { LoginService } from '../../services/login.service';
import { MotivesService } from '../../services/motives.service';
import { SettingsService } from '../../services/settings.service';
import { UserPreferenceService, UserPreferenceTypes } from '../../services/user-preference.service';
import { WindowService } from '../../services/window.service';
import { PSDate } from '../../utils/date.utils';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import { newProjectId } from '../studio/model/composition';
import { newStudioHref } from '../studio/state/studio-url';
import { openStudioReedit, studioProjectStillOpen, studioReeditHref } from '../studio/export/reedit';
import styles from './file-download.component.module.scss';
import { settlePageLanding } from './page-landing';

/**
 * Round 9 (L2, R8 QA #16): the Studio export's way back. The card's own "Re-edit" is a 78 × 27 key
 * among four (`file-list-item.component.tsx`, shared); the newest Studio card gets a PRIMARY "Back to
 * Studio" over the list, and the sentence says the project is still open there. Literal copy:
 * this page is not the Studio's (R9-L2 copy → studio.copy.ts if the Studio wants to own it).
 */
export const STUDIO_RETURN_COPY = Object.freeze({
    /** While the newest Studio card is still being made (or failed -- the card says so itself). */
    lead: 'Your video is being made here — the project is still open in the Studio.',
    /**
     * Round 10 (M2, R9 QA N5): once the card reads "Completed" the sentence follows it -- the row used
     * to keep "is being made" two lines above a finished card. R10-M2 copy → studio.copy.ts (NEEDS M1).
     */
    leadReady: 'Your video is ready — the project is still open in the Studio.',
    cta: 'Back to Studio',
});

export default class FileDownloadComponent extends Component<any> {
    public fileArray: FileUpload[] = [];
    /// This prevent SEO CLS jump
    private fileArrayCalculated = false;

    public recoveredFileUploads: FileUpload[] = [];
    public activeFileUploads: FileUpload[] = [];
    motives: MotiveConfig;

    public showHelpUsMessage = true;
    private isFreeUser = true;
    public internalAdsEnabled = false;
    public hasFiles = false;
    private bulkZipEnabled = false;

    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(MetricsService) private metricsService: MetricsService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(MotivesService) public motivesService: MotivesService;
    @lazyInject(LoginService) public loginService: LoginService;
    @lazyInject(BulkZipService) private bulkZipService: BulkZipService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(UserPreferenceService) private userPreferenceService: UserPreferenceService;
    @lazyInject(FileDownloadService) private fileDownloadService: FileDownloadService;

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.setScrollAnchoring(false);
        // Hidden for 7 days after dismissal — same pattern as the mobile-app
        // promo (MOBILE_APP_PROMO_CLOSED_DATE, 3 days).
        try {
            const closed = this.userPreferenceService.getPreference<Date>(UserPreferenceTypes.GROW_PITCH_CLOSED_DATE, Date);
            this.growPitchDismissed = closed != null && PSDate.from(closed).isAfter(PSDate.from().subtract(7, 'days'));
        } catch { this.growPitchDismissed = false; }
        if (!this.growPitchDismissed) {
            this.metricsService.emit(MetricEventName.GROW_PITCH_SHOWN, { page: 'downloads' });
            this.forceUpdate();
        }

        this.installDownloadsObservability();

        this.loginService.onSessionDetails
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (details) => {
                    this.isFreeUser = !details.isPaidUser;
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (motives) => {
                    this.showHelpUsMessage = this.isFreeUser && motives.headerPromotionToBuy;
                    this.internalAdsEnabled = this.motives && this.motives.internalMotivesEnabled;
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.fileManagerService.fileUploadsListChanged
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fileUploads) => {
                    this.updateFileArrayForDisplay(fileUploads);
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.fileManagerService.fileUploadChanged.pipe(
            untilDestroyed(this),
            debounceTime(100)
        ).subscribe({
            next: (fileUpload) => {
                this.forceUpdate();
            }
        })

        this.settingsService.featureFlags
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (ff) => {
                    const enabled = !!ff?.bulkZip;
                    if (enabled !== this.bulkZipEnabled) {
                        this.bulkZipEnabled = enabled;
                        this.forceUpdate();
                        if (enabled) this.watchBulkZipJobs();
                    }
                },
                error: (error) => console.error(error),
            });
    }

    componentDidUpdate() {
        // The first render with real content (the placeholder before it is a 1000 px block).
        if (this.fileArrayCalculated && !this.landed) {
            this.landed = true;
            settlePageLanding();
        }
    }

    componentWillUnmount() {
        this.teardownDownloadsObservability();
        this.setScrollAnchoring(true);
        // for untilDestroyed
    }

    // ── Landing ──
    // Round 7 (UX7 picker #1: the phone landed on the footer): after Convert in the frame picker the phone
    // arrived here with `body.scrollTop` 69 → 1965 at +100 ms and the card 1 241 px ABOVE the viewport.
    // Probed on QA 2026-09-20 (ux7/picker/t12b-landing-cause.mjs, every scroll/focus API trapped): nothing
    // scripted that scroll -- the only writes were React restoring `body.scrollTop = 69` at the click and
    // Next's `window.scrollTo(0, 0)` (a no-op: `<body>` is this site's scroller, globals.scss). It is the
    // browser's SCROLL ANCHORING: the picker's DOM (1 508 px) is swapped for this page's (3 405 px) in one
    // commit and Chrome keeps the shared footer at its viewport offset, so the body scrolls by the inserted
    // height. With `overflow-anchor: none` on the scroller the same probe stayed at 69. So: anchoring off on
    // the body while this page is mounted.
    //
    // Round 13 (Z4, owner review 5 P4: "I somehow end up toward the bottom of the downloads page … a new
    // page lands on the top; Back keeps the browser's position"): round 7 also brought the NEWEST CARD to the
    // top on every arrival, which put the header, the top ad and everything above the card out of view -- the
    // "toward the bottom" the owner saw. Gone. The landing is now the site-wide rule in `page-landing.ts`
    // (bound in _app.tsx): a push lands at the top, Back restores the remembered position, and this page
    // re-applies it once its real content has rendered (the placeholder before it is a 1000 px block).
    private landed = false;

    private setScrollAnchoring(on: boolean) {
        if (typeof document === 'undefined') return;
        try {
            if (on) document.body.style.removeProperty('overflow-anchor');
            else document.body.style.setProperty('overflow-anchor', 'none');
        } catch { /* no body style (SSR) */ }
    }


    updateFileArrayForDisplay(fileUploads: FileUpload[]) {
        this.fileArray = [];
        fileUploads.forEach(fileUpload => {
            this.fileArray.push(fileUpload);
        });
        this.fileArray = this.fileArray.sort((a, b) =>
            PSDate.from(a.processStartTime).isBefore(b.processStartTime) ? 1 : -1);
        this.activeFileUploads = this.fileArray.filter(f => !f.recoveredFileUpload);
        this.recoveredFileUploads = this.fileArray.filter(f => f.recoveredFileUpload);
        this.hasFiles = !!(this.recoveredFileUploads.length || this.activeFileUploads.length);
        this.fileArrayCalculated = true;
        this.forceUpdate();
    }

    showContactUs(e: MouseEvent) {
        e.preventDefault();
        ContactUsModal.show({});
    }

    private getAllOutputFileIds(): string[] {
        return this.fileArray.flatMap((fu) => {
            const outs = (fu.result?.outputFiles as string[] | undefined) || [];
            return outs.filter(Boolean);
        });
    }

    /**
     * Review 7 X1-05: the ready ZIPs this user already holds, so the "Download all" button can say what the new ZIP will
     * contain (the server leaves out files already in a still-ready ZIP, spec rule Z6). One list call when the flag is on;
     * the Downloads Center keeps `jobs$` current after that.
     */
    private bulkZipJobsWatched = false;

    private watchBulkZipJobs() {
        if (this.bulkZipJobsWatched) return;
        this.bulkZipJobsWatched = true;
        this.bulkZipService.jobs$.pipe(untilDestroyed(this)).subscribe(() => this.forceUpdate());
        this.bulkZipService.list().catch(() => { /* the label falls back to the plain count */ });
    }

    /** 'idle' | 'submitting' | 'error' — the button was silent in all three. */
    private zipState: 'idle' | 'submitting' | 'error' = 'idle';
    /** What actually went wrong, so the message can be true rather than generic. */
    private zipError: string | null = null;
    /** WC-10: the files a started ZIP had to leave out (this browser cannot prove them), named once. */
    private zipSkippedNote: string | null = null;

    /**
     * Ask for a ZIP of every output, and say what is happening.
     *
     * This used to fire the request and return: no busy state, no confirmation, no error -- the
     * failure path was a console.error nobody sees. The job does start, and it lands in the
     * Downloads Center behind the header pill, which on a phone is inside the hamburger. So from
     * the user's side a working request was indistinguishable from a dead button, and that is
     * exactly how it was reported.
     *
     * On success the Downloads Center opens, so the thing they asked for is on screen instead of
     * somewhere they have no reason to look.
     */
    private onBulkDownload = async () => {
        const ids = this.getAllOutputFileIds();
        if (ids.length < 2 || this.zipState === 'submitting') return;
        this.zipState = 'submitting';
        this.zipError = null;
        this.zipSkippedNote = null;
        this.forceUpdate();
        try {
            const resp = await this.bulkZipService.submit(ids);
            const skipped = resp?.skipped ?? [];
            if (skipped.length > 0) {
                const names = skipped.map((id) => id.split(/[\\/]/).pop() || id);
                this.zipSkippedNote = `${names.length === 1 ? '1 file' : `${names.length} files`} couldn't be added to this ZIP (${names.slice(0, 3).join(', ')}${names.length > 3 ? ', …' : ''}) -- download ${names.length === 1 ? 'it' : 'them'} individually.`;
            }
            this.zipState = 'idle';
            this.forceUpdate();
            // Put the job in front of them rather than leaving it in the header.
            this.bulkZipService.openCenter();
        } catch (err) {
            console.error('[bulk-zip] submit failed', err);
            // Say which thing went wrong. "Try again" is actively misleading for the
            // rate-limit case, where trying again is exactly what will not help.
            this.zipError = bulkZipErrorText(err);
            this.zipState = 'error';
            this.forceUpdate();
        }
    };

    getRotatingCTA() {
        const ctas = [
            <>⭐ If XConvert saved you time, a <a href="https://www.trustpilot.com/evaluate/xconvert.com" target="_blank" rel="noopener">quick Trustpilot review</a> helps us stay free.</>,
            <>📋 Know someone who needs this? <a href="/how-to-help-xconvert-to-grow">Share XConvert</a> and help others find us.</>,
            <>🙏 Enjoying XConvert? <a href="/how-to-help-xconvert-to-grow">Here are quick ways to help us grow</a>.</>,
        ];
        return ctas[Math.floor(Date.now() / 60000) % ctas.length];
    }

    // ── Downloads-page observability (2026-08-31 free-dl% investigation) ──
    // Answers "where do non-downloaders click instead?": PAGE_VIEW carries layout
    // facts, BTN_VISIBLE measures how long until the primary action is on screen,
    // PAGE_CLICK attributes every control tap, PAGE_EXIT summarizes on pagehide.
    private obsMountedAt = 0;
    private obsClicks: Record<string, number> = {};
    private obsBtnSeen = false;
    private obsClickHandler: ((e: MouseEvent) => void) | null = null;
    private obsExitHandler: (() => void) | null = null;
    private obsPoll: ReturnType<typeof setInterval> | null = null;

    private classifyClickTarget(el: Element | null): string | null {
        while (el && el !== document.body) {
            const jkey = (el as HTMLElement).dataset?.jkey;
            if (jkey) return jkey;
            const cls = (typeof el.className === 'string' ? el.className : '') || '';
            if (/grow-pitch-cta/.test(cls)) return 'pitch-cta';
            if (/grow-pitch-close/.test(cls)) return 'pitch-dismiss';
            if (/share-btn/.test(cls)) return 'share';
            if (/option-echo|processing-notes/.test(cls)) return 'card-info';
            if (el.tagName === 'BUTTON' || el.tagName === 'A') {
                const label = (el.textContent || '').trim().slice(0, 18).toLowerCase()
                    .replace(/[^a-z0-9 -]/g, '').trim().replace(/\s+/g, '-');
                return label || el.tagName.toLowerCase();
            }
            el = el.parentElement;
        }
        return null;
    }

    private installDownloadsObservability() {
        if (typeof window === 'undefined') return;
        this.obsMountedAt = Date.now();

        // Layout facts once the ad slots have had a chance to mount.
        setTimeout(() => {
            try {
                const card = document.querySelector('[data-jkey="file-list-item-card"]');
                const cardTop = card ? card.getBoundingClientRect().top + window.scrollY : -1;
                const ads = Array.from(document.querySelectorAll('ins.adsbygoogle, [id*="gcontainer"]'))
                    .map(a => a.getBoundingClientRect())
                    .filter(r => r.height > 20);
                const adAboveCard = cardTop >= 0 && ads.some(r => (r.top + window.scrollY) < cardTop);
                this.metricsService.emit(MetricEventName.DOWNLOADS_PAGE_VIEW, {
                    cards: this.activeFileUploads.length + this.recoveredFileUploads.length,
                    adAboveCard,
                    viewportW: window.innerWidth,
                });
            } catch { /* observability must never break the page */ }
        }, 1800);

        // Time until the FIRST card's Download button is actually on screen.
        this.obsPoll = setInterval(() => {
            try {
                if (this.obsBtnSeen) return;
                const btn = Array.from(document.querySelectorAll('button'))
                    .find(b => /^Download/.test((b.textContent || '').trim()));
                if (!btn) return;
                const r = btn.getBoundingClientRect();
                if (r.top >= 0 && r.bottom <= window.innerHeight && r.height > 0) {
                    this.obsBtnSeen = true;
                    if (this.obsPoll) { clearInterval(this.obsPoll); this.obsPoll = null; }
                    this.metricsService.emit(MetricEventName.DOWNLOAD_BTN_VISIBLE, {
                        ms: Date.now() - this.obsMountedAt,
                    });
                }
            } catch { /* never break the page */ }
        }, 500);

        // Every control interaction, attributed.
        this.obsClickHandler = (e: MouseEvent) => {
            try {
                const target = this.classifyClickTarget(e.target as Element);
                if (!target) return;
                this.obsClicks[target] = (this.obsClicks[target] || 0) + 1;
                this.metricsService.emit(MetricEventName.DOWNLOADS_PAGE_CLICK, { target });
                // A download click flips downloadedThisSession — re-render shortly so
                // the (post-download-gated) grow pitch shows up without waiting for
                // the next ws-driven render.
                if (/^download/.test(target)) setTimeout(() => { try { this.forceUpdate(); } catch { /* unmounted */ } }, 400);
            } catch { /* never break the page */ }
        };
        document.addEventListener('click', this.obsClickHandler, true);

        // Visit summary on the way out (metrics flush is sendBeacon/pagehide-safe).
        this.obsExitHandler = () => {
            try {
                this.metricsService.emit(MetricEventName.DOWNLOADS_PAGE_EXIT, {
                    downloadedAny: this.metricsService.downloadedThisSession,
                    btnWasVisible: this.obsBtnSeen,
                    clicks: JSON.stringify(this.obsClicks).slice(0, 400),
                    dwellMs: Date.now() - this.obsMountedAt,
                });
                // Beacon path, NOT flush(): fetch-based flush gets cancelled by the
                // navigation this very event describes.
                this.metricsService.flushOnUnload();
            } catch { /* never break the page */ }
        };
        window.addEventListener('pagehide', this.obsExitHandler);
    }

    private teardownDownloadsObservability() {
        if (this.obsClickHandler) document.removeEventListener('click', this.obsClickHandler, true);
        if (this.obsExitHandler) window.removeEventListener('pagehide', this.obsExitHandler);
        if (this.obsPoll) { clearInterval(this.obsPoll); this.obsPoll = null; }
    }

    private growPitchDismissed = true;

    dismissGrowPitch = () => {
        this.growPitchDismissed = true;
        try { this.userPreferenceService.setPreference(UserPreferenceTypes.GROW_PITCH_CLOSED_DATE, new Date()); } catch { }
        this.metricsService.emit(MetricEventName.GROW_PITCH_DISMISSED, {});
        this.forceUpdate();
    };

    getGrowPitch() {
        // GATED TO THE POST-DOWNLOAD MOMENT (owner decision 2026-08-31, free-dl%
        // regression investigation): before the first download, the only primary
        // CTA on this page must be Download — the pitch reads as the main action
        // when it sits above the result. After a download it's a natural thanks.
        // One ask, dismissible, only when there's a finished file to act on —
        // the rotator banner already covers the general help messages.
        if (this.growPitchDismissed || !this.hasFiles || !this.metricsService.downloadedThisSession) return null;
        return <div className={styles['grow-pitch']}>
            <div className={styles['grow-pitch-text']}>
                {'💚 '}<b>Have a blog, class page, wiki, or README?</b>{' One pasted line does more for us than anything else — it’s how new people find a free tool with no '}
                {'ad budget. Hit '}<b>&ldquo;Add us to your site&rdquo;</b>{' on any finished file below.'}
                <span className={styles['grow-pitch-thanks']}>🙏 Much appreciation from the XConvert team!</span>
            </div>
            <a className={styles['grow-pitch-cta']} href="/link-us"
                onClick={() => this.metricsService.emit(MetricEventName.GROW_PITCH_CTA_CLICKED, {})}>Get a link snippet →</a>
            <span className={styles['grow-pitch-close']} onClick={this.dismissGrowPitch} title="Dismiss">✕</span>
        </div>;
    }

    /**
     * Round 9 (L2, R8 QA #16): the newest card is a Studio export → one primary way back over the list.
     * `studioReeditHref` is the same predicate + target the card's Re-edit uses, so the two cannot
     * disagree; the click goes through Next's router (`openStudioReedit`), the href is the fallback.
     */
    private getStudioReturn() {
        // IT2 (saas N1): on a FULL load of /downloads every card is a "recovered" upload, so the
        // active list is empty and the row never showed -- the newest card is the newest card,
        // recovered or not (`fileArray` is already newest-first).
        const newest = this.fileArray[0];
        // IT1-W1 (saas #1): "the project is still open in the Studio" only while THIS browser still holds
        // the record (the Studio's own TTL reader decides) -- after 2.5 h, in another browser or after
        // cleared storage the row would send the user to an empty editor. `src=chip` names the source.
        const href = newest && studioProjectStillOpen(newest) ? studioReeditHref(newest, 'chip') : null;
        if (!href) return null;
        // BUG-06 follow-up (fix agent F, 2026-09-22): the only CTA here used to be "Back to Studio",
        // which reopens the SAME finished project -- a second export silently appends to the first
        // (BUG-08). "Make another video" mints a fresh sid on the same page/slug so a new project
        // never lands on top of the old one.
        const slug = newest?.pageData?.path || newest?.uniqueProcessType;
        // Review 7 S3-01: `src=new` -- an empty project, no "couldn't find that project" line.
        const makeAnotherHref = slug ? newStudioHref(slug, newProjectId()) : null;
        // R14 landscape: the layout utilities moved into the module (`.studio-return`) so a short
        // viewport can collapse the banner to one row -- Bootstrap's utilities are `!important`.
        return <div
            className={`${styles['studio-return']} bg-white border rounded`}
            data-jkey="studio-return"
            role="status"
        >
            <span className={styles['studio-return-lead']} data-jkey="studio-return-lead" data-state={newest.isCompleted() ? 'ready' : 'working'}>
                {newest.isCompleted() ? STUDIO_RETURN_COPY.leadReady : STUDIO_RETURN_COPY.lead}
            </span>
            <span className={styles['studio-return-keys']}>
                <a
                    className="btn btn-primary"
                    href={href}
                    data-jkey="studio-return-cta"
                    onClick={(e) => { if (openStudioReedit(newest, 'chip')) e.preventDefault(); }}
                >{STUDIO_RETURN_COPY.cta}</a>
                {makeAnotherHref && <a
                    className="btn btn-outline-primary"
                    href={makeAnotherHref}
                    data-jkey="studio-make-another-cta"
                >Make another video</a>}
            </span>
        </div>;
    }

    getFileLists(props) {
        return <div data-jkey="file-lists">
            {/* Review 7 M1-02: the grow pitch appears on the FIRST download click (downloadedThisSession); rendered here, above
                the cards, it pushed every card ~224 px down under the user's finger. It now renders below the list. */}
            {this.getStudioReturn()}
            <div>
                <FileListComponent {...props} sectionTitle="Downloadable Files" fileUploadArray={this.fileArray}
                    parentClasses="" cardClasses="">
                    {
                        <div className="gcontent-container">
                            <MotiveComponent
                                data={AdSlots.inContent} containerWidth="100%" containerHeight="60px"
                                location={MotiveLocationTypes.fileDownloadPageWithInCurrentDownloads} adWidth="720px" adHeight="60px"
                                showAds={true} className='width_major_md'
                            ></MotiveComponent>
                        </div>
                    }

                </FileListComponent>
            </div>
            {this.getGrowPitch()}
        </div>;
    }

    render() {
        if (!this.fileArrayCalculated) {
            return <div style={{ minHeight: '1000px' }}></div>;
        }
        return <>
            <Script type="text/javascript" dangerouslySetInnerHTML={{
                __html: `(function(d, s, id) {
var js, fjs = d.getElementsByTagName(s)[0];
if (d.getElementById(id)) return;
js = d.createElement(s); js.id = id;
js.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0";
fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'facebook-jssdk'));
`,
            }}
            >
            </Script>
            <div className={styles['wrapper']}>
                <div className={styles['main-card']}>
                    {/* BUG-06 (P2, manual-qa/review6/studio/review-notes.md): the payoff -- the
                        result card(s) -- must be the first thing in .main-card, ahead of every ad
                        block. It used to sit behind a 90px pageTop ad and a 600px rail ad slot that
                        was still active at 900px (d-md-block, widened to d-lg-block below), landing
                        the Download button at y≈873 in a 900px viewport. Ads are not removed, only
                        reordered/regated. */}
                    {this.bulkZipEnabled && this.getAllOutputFileIds().length >= 2 && (
                        <div className="mb-3">
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={this.zipState === 'submitting'}
                                aria-busy={this.zipState === 'submitting'}
                                onClick={this.onBulkDownload}
                            >
                                {this.zipState === 'submitting'
                                    ? 'Preparing your ZIP…'
                                    : bulkZipButtonText(bulkZipPlan(this.getAllOutputFileIds(), this.bulkZipService.jobs$.getValue()))}
                            </button>
                            {(() => {
                                const note = bulkZipCoveredNote(bulkZipPlan(this.getAllOutputFileIds(), this.bulkZipService.jobs$.getValue()));
                                return note && <p className="text-muted mb-0 mt-2" data-jkey="bulk-zip-covered-note" style={{ fontSize: '0.85rem' }}>{note}</p>;
                            })()}
                            {this.zipSkippedNote && (
                                <p role="status" className="text-muted mb-0 mt-2" data-jkey="bulk-zip-skipped-note" style={{ fontSize: '0.85rem' }}>
                                    {this.zipSkippedNote}
                                </p>
                            )}
                            {this.zipState === 'error' && this.zipError && (
                                <p role="status" className="text-danger mb-0 mt-2" style={{ fontSize: '0.85rem' }}>
                                    {this.zipError}
                                </p>
                            )}
                        </div>
                    )}
                    {
                        this.getFileLists(this.props)
                    }

                    <div>
                        {
                            <div className="">
                                <MotiveComponent
                                    data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px"
                                    location={MotiveLocationTypes.fileDownloadPageTop} adWidth="970px" adHeight="90px" showAds={true} className='width_major'
                                ></MotiveComponent>
                            </div>
                        }
                    </div>

                    {
                        this.showHelpUsMessage && <div className={styles['help-message']}>
                            {this.getRotatingCTA()}
                        </div>
                    }

                    <div className='row mt-3'>
                        {/* Widened from d-md-block: at 900px (>=768, <992) this 600px slot was still
                            active and stacking above the fold alongside the content. lg (>=992) keeps
                            it for genuinely wide desktop viewports only. */}
                        <div className='col-md-2 d-none d-lg-block'>
                            {
                                <div className="">
                                    <MotiveComponent
                                        data={AdSlots.desktopRail} containerWidth="100%" containerHeight="600px"
                                        location={MotiveLocationTypes.fileDownloadPageLeft} adWidth="160px" adHeight="600px" showAds={true} className='height_major'
                                    ></MotiveComponent>
                                </div>
                            }
                        </div>
                        <div className="col-12 col-md-8">
                            <div>
                                <div>
                                    {
                                        !this.hasFiles && <p className="text-muted mt-3">
                                            No files found. Your processed and in-progress files will appear here.
                                        </p>
                                    }

                                    <div className="mt-5">
                                        <h5>About My Downloads</h5>
                                        <p className="text-secondary">
                                            This page displays your in-progress and processed files. Use the controls on each card to download, re-edit, or check the status of your files.
                                        </p>
                                    </div>
                                </div>

                                <div className={styles['faq-section']}>
                                    <h5>Frequently Asked Questions</h5>

                                    <div className={styles['faq-item']}>
                                        <strong>Why is my download slow?</strong>
                                        <p>Network speed usually depends on the distance between you and our servers. We do our best to route you to the nearest server, but internet traffic can affect speeds.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Why is the compressed file sometimes larger?</strong>
                                        <p>Compression results depend on the file content and settings. Occasionally, certain settings may increase file size. Try using the <b>Re-edit</b> button to adjust compression levels.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Are my files private?</strong>
                                        <p>Yes. We only store files for 2.5 hours before they are permanently deleted. You can also manually delete files instantly using the trash icon.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Can I close the browser while processing?</strong>
                                        <p>Yes. Once processing starts, you can close the tab. If you return to this page within 2.5 hours, your files will be here ready for download.</p>
                                    </div>
                                </div>

                                <div className={styles['convert-another']}>
                                    {/* Was eight fixed pills that ignored what the user had just made.
                                        Now the first row is what people who produced THIS output type
                                        actually did next, measured over 14 days of production switches
                                        (docs/research/next-tool-transitions.md). The generic tools stay
                                        underneath, since not every visit is a continuation. */}
                                    {/* Per-file continuations moved onto the result cards, where
                                        they can name the file they act on. This block is back to
                                        being what it always was: somewhere to start something
                                        new. */}
                                    <h5>Convert Another File</h5>
                                    <p className="text-secondary" style={{fontSize: '0.9rem'}}>
                                        Need to process more files? Jump back to one of our popular tools.
                                    </p>
                                    <div className={styles['tools-grid']}>
                                        {[
                                            ['Image Compressor', 'image-compressor'], ['Video Compressor', 'video-compressor'],
                                            ['Audio Compressor', 'audio-compressor'], ['Compress PDF', 'compress-pdf'],
                                            ['Image Converter', 'image-converter'], ['Video Converter', 'video-converter'],
                                            ['Audio Converter', 'audio-converter'], ['Merge to PDF', 'merge-image-to-pdf'],
                                        ].map(([name, slug], i) => <a key={i} href={`/${slug}`} className={styles['tool-pill']}>{name}</a>)}
                                    </div>
                                </div>                            </div>
                        </div>

                        <div className='col-md-2 d-none d-lg-block'>
                            {
                                <div className="">
                                    <MotiveComponent
                                        data={AdSlots.desktopRail} containerWidth="100%" containerHeight="600px"
                                        location={MotiveLocationTypes.fileDownloadPageRight} adWidth="160px" adHeight="600px" showAds={true} className='height_major'
                                    ></MotiveComponent>
                                </div>
                            }
                        </div>
                    </div>

                    <div>
                        <div>
                            <div>
                                {
                                    <div className="">
                                        <MotiveComponent
                                            data={AdSlots.aboveFooter} containerWidth="100%" containerHeight="90px"
                                            location={MotiveLocationTypes.fileDownloadPageBottom} adWidth="970px" adHeight="90px" showAds={true}
                                            className='width_major'
                                        ></MotiveComponent>
                                    </div>
                                }

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    }
}
